import type { WebContext } from "@reboot-dev/reboot-web";
import { collabKey, getVersion } from "@stepwisehq/prosemirror-collab-commit/collab-commit";
import type { EditorState } from "prosemirror-state";

import { defer } from "@moment/api-collab";
import { Logger } from "@moment/logging";
import type { PresenceStore } from "@moment/reboot-differential";

import { prosemirrorClientId } from "../../prosemirror/client-id";
import type { PresenceIndicator } from "../../prosemirror/presence.types";

const logger = new Logger("presence-utils");

/**
 * Creates an async generator that yields presence indicators from Reboot.
 *
 * Similar to `makeMdPageRemoteChangesCommand` but for presence.
 *
 * Faithfully, lightly adapted from page.machine.ts lines 525-536, which corresponds to the
 * `presence()` function in `listenForChanges`.
 */
export function makeMdPageReceivePresenceCommand(
	presence: PresenceStore.WeakReference,
	context: WebContext
): (ops: { signal: AbortSignal }) => AsyncGenerator<Record<string, PresenceIndicator>> {
	return async function* (ops) {
		const signal = ops.signal;

		const [stream] = await presence.reactively().getPresence(context, undefined, { signal });
		for await (const { indicators } of stream) {
			if (signal.aborted) {
				break;
			}

			yield indicators;
		}

		logger.debug("Presence listener ended");
	};
}

/**
 * Creates an updatePresence callback that sends local cursor position to Reboot.
 *
 * Handles rebasing cursor position through unconfirmed changes before sending, so the server
 * receives positions relative to the confirmed document state.
 *
 * Faithfully, lightly adapted from page.machine.ts lines 588-621, which corresponds to the
 * `sendPresenceToReboot` actor.
 */
export function makeMdPageUpdatePresenceCommand(
	presence: PresenceStore.WeakReference,
	context: WebContext,
	userId: string
): (editorState: EditorState) => Promise<void> {
	return async (editorState) => {
		const { head, anchor } = editorState.selection;
		const version = getVersion(editorState) ?? 0;

		const indicator: PresenceIndicator = {
			head,
			anchor,
			version,
			clientId: prosemirrorClientId,
			userId,
		};

		// Rebase through unconfirmed changes so the server receives positions relative to the
		// confirmed document state.
		const collabState = collabKey.getState(editorState);
		if (!collabState) {
			logger.warn("updatePresence: No collab state found, skipping");
			return;
		}

		const { unconfirmed } = collabState;
		for (const rebaseable of unconfirmed.toReversed()) {
			indicator.head = rebaseable.inverted.getMap().map(indicator.head);
			indicator.anchor = rebaseable.inverted.getMap().map(indicator.anchor);
		}

		await presence.updatePresence(context, { indicator });
	};
}

/**
 * Creates a connect callback that registers this client as viewing the document.
 *
 * This is a mechanism for the server to track which clients are connected. Should be called when
 * the editor component mounts, and aborted when it unmounts.
 *
 * Faithfully, lightly adapted from page.machine.ts lines 514-523, which corresponds to the
 * `connect()` call in `listenForChanges`.
 */
export function makeMdPageConnectPresenceCommand(
	presence: PresenceStore.WeakReference,
	context: WebContext
): (ops: { signal: AbortSignal }) => Promise<void> {
	return async function (ops) {
		logger.debug("Connecting to presence", { clientId: prosemirrorClientId, sig: ops.signal });
		const [connect] = await presence
			.reactively()
			.connect(context, { clientId: prosemirrorClientId }, { signal: ops.signal });

		const disconnected = defer<void>();
		(async function () {
			for await (const result of connect) {
				//
				// NOTE: THIS BLOCK SHOULD NEVER BE EXECUTED! It is included entirely defensively.
				// Some various notes:
				//
				//   - The whole point of the `connect` RPC is that it should NEVER, EVER return:
				//     the user is "present" as long as the request is open.
				//   - If the request terminates, the underlying Reboot stream will transparently
				//     retry it.
				//   - If the request is cancelled via the abort signal, the iterator will complete
				//     and we will STILL not execute this body.
				//   - Thus, if we get here, something extremely wacky has happened. So we'll just
				//     something relatively normal, which is check the abort signal and go back to
				//     waiting.
				//

				const aborted = ops.signal.aborted;
				if (aborted) {
					break;
				}

				logger.debug("Reconnecting to presence server...", { aborted, result });
			}
			disconnected.resolve();
		})();

		await disconnected;

		logger.debug("Disconnected from presence server", { aborted: ops.signal.aborted });
	};
}
